<template>
  <v-content class="supervisor-review page">
    <v-progress-linear
      v-if="loading"
      :color="themes.candidate.light.loading"
      :height="5"
      indeterminate
    />
    <v-container v-else>
      <v-layout justify-center>
        <v-flex xs12 sm8 md6>
          <v-card class="mt-3">
            <v-toolbar card :color="themes.candidate.light.dialogTitle">
              <v-toolbar-title class="headline">
                Experience Review
              </v-toolbar-title>
            </v-toolbar>

            <v-card-text v-if="error">
              <p>{{ error }}</p>
            </v-card-text>

            <template v-else>
              <v-card-text v-if="supervisor && experience">
                <p>
                  Hi <strong>{{ supervisor.name }}</strong
                  >, please review the experience details below for
                  {{ experience.profile && experience.profile.name }}.
                </p>
              </v-card-text>

              <v-divider />

              <v-card-text v-if="experience">
                <div class="mb-2">
                  <div class="subtitle-1 font-weight-bold">Candidate</div>
                  <div>{{ experience.profile && experience.profile.name }}</div>
                </div>

                <div class="mb-2">
                  <div class="subtitle-1 font-weight-bold">Role</div>
                  <div>{{ experience.position }}</div>
                </div>

                <div class="mb-2">
                  <div class="subtitle-1 font-weight-bold">Organization</div>
                  <div>
                    {{
                      experience.organization && experience.organization.name
                    }}
                    <span
                      v-if="
                        experience.organization &&
                          experience.organization.industry
                      "
                    >
                      ({{ experience.organization.industry }})
                    </span>
                  </div>
                </div>

                <div class="mb-2">
                  <div class="subtitle-1 font-weight-bold">Dates</div>
                  <div>
                    <span v-if="experience.startsAt">
                      {{ new Date(experience.startsAt) | date("d MMMM yyy") }}
                    </span>
                    <span v-else>
                      Not specified
                    </span>
                    &mdash;
                    <span v-if="experience.endsAt">
                      {{ new Date(experience.endsAt) | date("d MMMM yyy") }}
                    </span>
                    <span v-else>
                      Present
                    </span>
                  </div>
                </div>

                <div
                  v-if="
                    experience.responsibilities &&
                      experience.responsibilities.length
                  "
                >
                  <div class="subtitle-1 font-weight-bold mb-1">
                    Responsibilities
                  </div>
                  <ul class="pl-4">
                    <li
                      v-for="(item, index) in experience.responsibilities"
                      :key="`resp-${index}`"
                    >
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </v-card-text>

              <v-card-text v-if="isReadOnly">
                <v-alert :type="statusAlertType" outline>
                  {{ statusMessage }}
                </v-alert>

                <div v-if="supervisor && supervisor.feedback" class="mt-3">
                  <div class="subtitle-1 font-weight-bold mb-1">
                    Your feedback
                  </div>
                  <div class="body-1">{{ supervisor.feedback }}</div>
                </div>
              </v-card-text>

              <v-card-text v-else>
                <p class="mb-3">
                  If everything looks correct, click
                  <strong class="font-weight-bold">Approve</strong>. If anything
                  is incomplete or inaccurate, please let us know in the box
                  below.
                </p>

                <v-textarea
                  v-model="feedback"
                  label="Feedback or corrections (optional)"
                  :color="themes.candidate.light.input"
                  auto-grow
                  rows="3"
                />
              </v-card-text>

              <v-card-actions v-if="!isReadOnly">
                <v-spacer />
                <v-btn
                  :color="themes.candidate.light.successButton"
                  :loading="action === 'approve' && saving"
                  :disabled="saving"
                  @click="submit('approve')"
                >
                  Approve
                </v-btn>
                <v-btn
                  :color="
                    themes.candidate.light.dangerButton ||
                      themes.candidate.light.danger
                  "
                  :loading="action === 'feedback' && saving"
                  :disabled="saving || !feedback"
                  @click="submit('feedback')"
                  class="ml-2"
                >
                  Submit feedback
                </v-btn>
              </v-card-actions>
            </template>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
import Vue from "vue";
// Mixins
import { managedDocumentAttributes } from "@/mixins/managedDocumentAttributes.js";

export default {
  mixins: [managedDocumentAttributes],

  created() {
    this.documentAttributes.title = "Experience review - Leaply";

    if (!this.token) {
      this.error = "This review link is invalid or has expired.";
      this.loading = false;
      return;
    }

    this.fetchReview();
  },

  data() {
    return {
      token: this.$route && this.$route.query && this.$route.query.token,
      loading: true,
      saving: false,
      action: null,
      supervisor: null,
      experience: null,
      feedback: "",
      error: null
    };
  },

  computed: {
    isReadOnly() {
      if (!this.supervisor) return false;
      const verifiedAt =
        this.supervisor.verifiedAt || this.supervisor.verified_at;
      if (verifiedAt) return true;
      if (this.supervisor.feedback) return true;
      return false;
    },

    statusAlertType() {
      if (!this.supervisor) return "info";
      const verifiedAt =
        this.supervisor.verifiedAt || this.supervisor.verified_at;
      if (verifiedAt && !this.supervisor.feedback) return "success";
      if (this.supervisor.feedback) return "error";
      return "info";
    },

    statusMessage() {
      if (!this.supervisor) return "";
      const verifiedAt =
        this.supervisor.verifiedAt || this.supervisor.verified_at;
      if (verifiedAt && !this.supervisor.feedback) {
        return "This experience has been approved. No further changes can be made with this link.";
      }
      if (this.supervisor.feedback) {
        return "Your feedback has been recorded. No further changes can be made with this link.";
      }
      return "";
    }
  },

  methods: {
    fetchReview() {
      this.loading = true;
      this.error = null;

      return Vue.$http
        .get(`supervisor_reviews/${this.token}`)
        .then(({ data }) => {
          this.supervisor = data.supervisor;
          this.experience = data.experience;
          if (this.supervisor && this.supervisor.feedback) {
            this.feedback = this.supervisor.feedback;
          }
        })
        .catch(error => {
          if (error && error.response && error.response.status === 404) {
            this.error = "This review link is invalid or has expired.";
          } else {
            this.error =
              "We could not load this review. Please try again later.";
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    submit(kind) {
      if (!this.token || this.saving) return;
      if (kind === "feedback" && !this.feedback) return;

      this.saving = true;
      this.action = kind;

      const payload =
        kind === "feedback" && this.feedback
          ? { supervisor: { feedback: this.feedback } }
          : {};

      return Vue.$http
        .patch(`supervisor_reviews/${this.token}`, payload)
        .then(({ data }) => {
          this.supervisor = data.supervisor;
          this.experience = data.experience;
          if (this.supervisor && this.supervisor.feedback) {
            this.feedback = this.supervisor.feedback;
          }

          if (kind === "approve") {
            this.$store.dispatch(
              "snackbarSuccess",
              "Thank you. This experience has been approved."
            );
          } else {
            this.$store.dispatch(
              "snackbarSuccess",
              "Thank you. Your feedback has been recorded."
            );
          }
        })
        .catch(() => {
          this.$store.dispatch(
            "snackbarError",
            "We could not save your response. Please try again."
          );
        })
        .finally(() => {
          this.saving = false;
          this.action = null;
        });
    }
  }
};
</script>
